import React, { useState } from 'react';
import { Card, Row, Col, Form, Button, Badge, Alert, Modal } from 'react-bootstrap';
import { FaPlus, FaTrash, FaReceipt } from 'react-icons/fa';
import { expenseTypes } from './VehicleExpenseForm';

/**
 * AddVehiclePage için özel masraf ekleme formu
 * API çağrısı yapmaz, sadece state management
 * 
 * Path: components/forms/ExpenseAddForm.js
 */
const ExpenseAddForm = ({ addedExpenses, setAddedExpenses }) => {
    const [showModal, setShowModal] = useState(false);
    const [currentExpense, setCurrentExpense] = useState({
        type: '',
        description: '',
        amount: '',
        date: new Date().toISOString().split('T')[0],
        vendor: '',
        receiptNumber: ''
    });
    const [formError, setFormError] = useState('');


    // 🔥 DEBUG: Component render kontrolü
    console.log('🔥 ExpenseAddForm RENDER:', {
        showModal,
        addedExpensesCount: addedExpenses.length
    });

    // Form input değişikliği
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setCurrentExpense(prev => ({
            ...prev,
            [name]: value
        }));
        setFormError('');
    };

    // Masraf ekleme
    const handleAddExpense = (e) => {
        e.preventDefault();

        // Validation
        if (!currentExpense.type) {
            setFormError('Masraf türü seçin');
            return;
        }

        if (!currentExpense.description.trim()) {
            setFormError('Açıklama girin');
            return;
        }

        if (!currentExpense.amount || parseFloat(currentExpense.amount) <= 0) {
            setFormError('Geçerli bir tutar girin');
            return;
        }

        // Yeni masraf objesi
        const newExpense = {
            id: `exp_${Date.now()}_${Math.random()}`,
            type: currentExpense.type,
            description: currentExpense.description.trim(),
            amount: parseFloat(currentExpense.amount),
            date: currentExpense.date,
            vendor: currentExpense.vendor.trim(),
            receiptNumber: currentExpense.receiptNumber.trim(),
            addedDate: new Date()
        };

        // State'e ekle
        setAddedExpenses(prev => [...prev, newExpense]);

        // Form'u temizle
        setCurrentExpense({
            type: '',
            description: '',
            amount: '',
            date: new Date().toISOString().split('T')[0],
            vendor: '',
            receiptNumber: ''
        });

        setShowModal(false);
        setFormError('');
    };

    // Masraf silme
    const handleRemoveExpense = (expenseId) => {
        setAddedExpenses(prev => prev.filter(exp => exp.id !== expenseId));
    };

    // Modal kapatma
    const handleCloseModal = () => {
        setShowModal(false);
        setFormError('');
        setCurrentExpense({
            type: '',
            description: '',
            amount: '',
            date: new Date().toISOString().split('T')[0],
            vendor: '',
            receiptNumber: ''
        });
    };

    // Toplam masraf hesapla
    const getTotalExpenses = () => {
        return addedExpenses.reduce((total, exp) => total + exp.amount, 0);
    };

    // Masraf türü bilgisini getir
    const getExpenseTypeInfo = (typeValue) => {
        return expenseTypes.find(type => type.value === typeValue);
    };

    // Tutar formatla
    const formatAmount = (amount) => {
        return new Intl.NumberFormat('tr-TR', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2
        }).format(amount);
    };

    return (
        <>
            <Card className="mb-4">
                <Card.Header>
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <h5 className="mb-0">💸 Masraf Yönetimi</h5>
                            <small className="text-muted">
                                Araç ile ilgili masrafları ekleyin
                            </small>
                        </div>
                        <Button
                            variant="outline-success"
                            size="sm"
                            // Şununla değiştir:
                            onClick={() => {
                                console.log('🔥 MASRAF EKLE BUTTON CLICKED!');
                                console.log('🔥 Current showModal:', showModal);
                                setShowModal(true);
                                console.log('🔥 setShowModal(true) called');
                            }}                        >
                            <FaPlus className="me-1" />
                            Masraf Ekle
                        </Button>
                    </div>
                </Card.Header>
                <Card.Body>
                    {/* Masraf özeti */}
                    <Row className="mb-3">
                        <Col md={4} className="text-center">
                            <div className="bg-info text-white rounded p-2">
                                <strong>{addedExpenses.length}</strong>
                                <small className="d-block">Toplam Masraf</small>
                            </div>
                        </Col>
                        <Col md={4} className="text-center">
                            <div className="bg-danger text-white rounded p-2">
                                <strong>{formatAmount(getTotalExpenses())} ₺</strong>
                                <small className="d-block">Toplam Tutar</small>
                            </div>
                        </Col>
                        <Col md={4} className="text-center">
                            <div className="bg-success text-white rounded p-2">
                                <strong>
                                    {addedExpenses.length > 0 ? formatAmount(getTotalExpenses() / addedExpenses.length) : '0'} ₺
                                </strong>
                                <small className="d-block">Ortalama</small>
                            </div>
                        </Col>
                    </Row>

                    {/* Masraflar listesi */}
                    {addedExpenses.length > 0 ? (
                        <div className="table-responsive">
                            <table className="table table-sm table-hover">
                                <thead className="table-light">
                                    <tr>
                                        <th>Tür</th>
                                        <th>Açıklama</th>
                                        <th>Tutar</th>
                                        <th>Tarih</th>
                                        <th>Satıcı</th>
                                        <th>İşlem</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {addedExpenses.map(expense => {
                                        const typeInfo = getExpenseTypeInfo(expense.type);

                                        return (
                                            <tr key={expense.id}>
                                                <td>
                                                    <div className="d-flex align-items-center">
                                                        <FaReceipt
                                                            className={`text-secondary me-2`}
                                                            size={16}
                                                        />
                                                        <div>
                                                            <div style={{ fontSize: '0.9em', fontWeight: 'bold' }}>
                                                                {typeInfo?.label || expense.type}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div style={{ fontSize: '0.9em' }}>
                                                        {expense.description}
                                                    </div>
                                                </td>
                                                <td>
                                                    <Badge bg="success">
                                                        {formatAmount(expense.amount)} ₺
                                                    </Badge>
                                                </td>
                                                <td style={{ fontSize: '0.8em' }}>
                                                    {new Date(expense.date).toLocaleDateString('tr-TR')}
                                                </td>
                                                <td style={{ fontSize: '0.8em' }}>
                                                    {expense.vendor || '-'}
                                                </td>
                                                <td>
                                                    <Button
                                                        variant="outline-danger"
                                                        size="sm"
                                                        onClick={() => handleRemoveExpense(expense.id)}
                                                    >
                                                        <FaTrash size={12} />
                                                    </Button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="text-center py-4 text-muted">
                            <FaReceipt size={40} className="mb-3 text-muted" />
                            <p>Henüz masraf eklenmedi</p>
                            <Button
                                variant="outline-primary"
                                onClick={() => setShowModal(true)}
                            >
                                <FaPlus className="me-1" />
                                İlk masrafı ekle
                            </Button>
                        </div>
                    )}
                </Card.Body>
            </Card>

            {/* Masraf Ekleme Modal'ı */}
            <Modal show={showModal} onHide={handleCloseModal} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FaPlus className="me-2" />
                        Yeni Masraf Ekle
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {formError && (
                        <Alert variant="danger">{formError}</Alert>
                    )}

                    <Form onSubmit={handleAddExpense}>
                        {/* Masraf Türü Seçimi - DROPDOWN */}
                        <Form.Group className="mb-3">
                            <Form.Label>Masraf Türü *</Form.Label>
                            <Form.Select
                                name="type"
                                value={currentExpense.type}
                                onChange={handleInputChange}
                                required
                            >
                                <option value="">Masraf türü seçin</option>
                                {expenseTypes.map(type => (
                                    <option key={type.value} value={type.value}>
                                        {type.label}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>

                        <Row>
                            {/* Açıklama */}
                            <Col md={8}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Açıklama *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="description"
                                        value={currentExpense.description}
                                        onChange={handleInputChange}
                                        placeholder="Masraf açıklaması..."
                                        required
                                    />
                                </Form.Group>
                            </Col>

                            {/* Tutar */}
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Tutar (₺) *</Form.Label>
                                    <Form.Control
                                        type="number"
                                        name="amount"
                                        value={currentExpense.amount}
                                        onChange={handleInputChange}
                                        placeholder="0.00"
                                        step="0.01"
                                        min="0"
                                        required
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            {/* Tarih */}
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Tarih</Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="date"
                                        value={currentExpense.date}
                                        onChange={handleInputChange}
                                    />
                                </Form.Group>
                            </Col>

                            {/* Satıcı */}
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Satıcı/Firma</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="vendor"
                                        value={currentExpense.vendor}
                                        onChange={handleInputChange}
                                        placeholder="Satıcı firma adı..."
                                    />
                                </Form.Group>
                            </Col>

                            {/* Fiş No */}
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Fiş/Fatura No</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="receiptNumber"
                                        value={currentExpense.receiptNumber}
                                        onChange={handleInputChange}
                                        placeholder="Fiş numarası..."
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseModal}>
                        İptal
                    </Button>
                    <Button
                        variant="success"
                        onClick={handleAddExpense}
                        disabled={!currentExpense.type || !currentExpense.description || !currentExpense.amount}
                    >
                        <FaPlus className="me-1" />
                        Masraf Ekle
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default ExpenseAddForm;